import { TextField } from '@mui/material'
import { MRT_RowData } from 'material-react-table'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import { textCellKeyDownHandler } from '../helpers'
import { TextFieldSx } from '../styles'
import { type MrtCellProps } from '../types'
import { getInitCellValue } from '../utils'
import { CellTooltip } from './CellTooltip'

const MIN_TOOLTIP_LENGTH = 13

interface TextCellProps<TData extends MRT_RowData> extends MrtCellProps<TData> {
	onCommit?: (value: string, rowIndex: number) => void
}

export const TextCell = <TData extends MRT_RowData>({
	cell,
	row,
	column,
	updateCellValue,
	customValue,
	isDisabled = false,
	isReadOnly = false,
	onCommit,
}: TextCellProps<TData>) => {
	const [value, setValue] = useState<string>('')

	const cellValue = cell?.getValue()

	// Track initial value and commit state to avoid redundant updates/double commits
	const initialValueRef = useRef<string>('')
	const hasValueChangedRef = useRef<boolean>(false)
	const hasCommittedRef = useRef<boolean>(false)

	// Update local state when cell value or custom value changes
	useEffect(() => {
		const initialValue = getInitCellValue(customValue, cellValue)
		setValue(initialValue)
		initialValueRef.current = initialValue
		hasValueChangedRef.current = false
		hasCommittedRef.current = false
	}, [cellValue, customValue])

	// Single commit function
	const commitIfChanged = useCallback(
		(nextValue: string) => {
			// prevent double commit (e.g., key commit then blur)
			if (hasCommittedRef.current || !hasValueChangedRef.current) return

			if (updateCellValue && row?.index !== undefined && column?.id) {
				updateCellValue(row.index, column.id, nextValue)
				hasCommittedRef.current = true
				hasValueChangedRef.current = false
				initialValueRef.current = nextValue
				// Notify parent after commit (e.g., for auto-generating code from name)
				onCommit?.(nextValue, row.index)
			}
		},
		[updateCellValue, row?.index, column?.id, onCommit],
	)

	// Memoize blur handler: fallback commit if changed
	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			commitIfChanged(event.target.value)
		},
		[commitIfChanged],
	)

	// Memoize change handler
	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const nextValue = event.target.value
			setValue(nextValue)
			hasValueChangedRef.current = nextValue !== initialValueRef.current
		},
		[],
	)

	// Handle Tab/Enter as explicit commit actions
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			textCellKeyDownHandler(event)
			if (event.key === 'Tab') {
				commitIfChanged((event.target as HTMLInputElement).value)
			}
		},
		[commitIfChanged],
	)

	const showTooltip = value.length > MIN_TOOLTIP_LENGTH

	return (
		<CellTooltip
			title={showTooltip ? value : undefined}
			isEditMode={!isDisabled && !isReadOnly}
		>
			<TextField
				disabled={isDisabled}
				fullWidth
				onBlur={handleBlur}
				onChange={handleChange}
				slotProps={{
					input: {
						readOnly: isReadOnly,
						onKeyDown: handleKeyDown,
						disableUnderline: isDisabled,
					},
					htmlInput: {
						'data-testid': `table-text-${row.index}_${column.id}`,
					},
				}}
				sx={TextFieldSx(isReadOnly, isDisabled)}
				type="text"
				value={value}
				variant="standard"
			/>
		</CellTooltip>
	)
}
